<!-- B05 机构服务订单（B2） -->
<template>
  <div>
    <el-card shadow="never" class="mb-16px">
      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <el-tab-pane label="待处理" name="PENDING" />
        <el-tab-pane label="进行中" name="PROCESSING" />
        <el-tab-pane label="已完成" name="COMPLETED" />
        <el-tab-pane label="退款中" name="REFUNDING" />
      </el-tabs>
    </el-card>

    <el-card shadow="never">
      <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="68px">
        <el-form-item label="订单号">
          <el-input
            v-model="queryParams.no"
            class="!w-220px"
            clearable
            placeholder="请输入订单号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="创建时间">
          <el-date-picker
            v-model="queryParams.createTime"
            :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
            class="!w-280px"
            end-placeholder="结束日期"
            start-placeholder="开始日期"
            type="daterange"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column label="订单号" min-width="180" prop="no" show-overflow-tooltip />
        <el-table-column label="服务名称" min-width="160" prop="serviceName" show-overflow-tooltip />
        <el-table-column label="预约信息" min-width="200">
          <template #default="{ row }">
            <div v-if="row.appointmentDate">
              {{ row.appointmentDate }} {{ row.appointmentTime || '' }}
            </div>
            <div v-if="row.serviceAddress" class="text-12px text-gray-500">{{ row.serviceAddress }}</div>
            <span v-if="!row.appointmentDate && !row.serviceAddress" class="text-gray-400">—</span>
          </template>
        </el-table-column>
        <el-table-column label="联系人" width="120">
          <template #default="{ row }">
            <div>{{ row.contactName || '—' }}</div>
            <div class="text-12px text-gray-500">{{ row.contactPhone || '' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="师傅" width="100" prop="providerName">
          <template #default="{ row }">{{ row.providerName || '待派单' }}</template>
        </el-table-column>
        <el-table-column label="实付金额" width="110">
          <template #default="{ row }">{{ fenToYuan(row.payPrice) }} 元</template>
        </el-table-column>
        <el-table-column label="订单状态" width="140">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.status)">{{ statusLabel(row.status) }}</el-tag>
            <el-tag v-if="row.refunding" type="danger" size="small" class="ml-4px">退款中</el-tag>
            <el-tag v-if="row.slaTimeout" type="warning" size="small" class="ml-4px">SLA 超时</el-tag>
            <el-tag v-if="row.providerRejected" type="danger" size="small" class="ml-4px">师傅拒单</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170" prop="createTime" :formatter="dateFormatter" />
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
            <el-button
              v-if="activeTab === 'PENDING' && canReturn(row)"
              link
              type="warning"
              @click="openReturnDialog(row)"
            >
              退回平台
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-drawer v-model="detailVisible" :title="detailTitle" size="640px">
      <div v-loading="detailLoading">
        <template v-if="detail">
          <div v-if="canAssign(detail) || canReturn(detail)" class="mb-12px">
            <el-button v-if="canAssign(detail)" type="primary" @click="openAssignDialog">内派师傅</el-button>
            <el-button v-if="canReturn(detail)" type="warning" plain @click="openReturnDialog(detail)">
              退回平台
            </el-button>
          </div>
          <el-alert
            v-if="depositSatisfied === false"
            class="mb-12px"
            :closable="false"
            show-icon
            title="保证金已无可用余额，无法内派师傅。请线下打款后联系平台确认入账。"
            type="error"
          />
          <el-descriptions :column="1" border title="订单信息">
            <el-descriptions-item label="订单号">{{ detail.no }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag :type="statusTagType(detail.status)">{{ statusLabel(detail.status) }}</el-tag>
              <el-tag v-if="detail.refunding" type="danger" size="small" class="ml-4px">退款中</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="履约方式">{{ fulfillmentLabel(detail.fulfillmentType) }}</el-descriptions-item>
            <el-descriptions-item label="实付金额">{{ fenToYuan(detail.payPrice) }} 元</el-descriptions-item>
            <el-descriptions-item label="师傅">{{ detail.providerName || '待派单' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
            <el-descriptions-item label="支付时间">{{ formatDateTime(detail.payTime) || '—' }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ detail.remark || '—' }}</el-descriptions-item>
          </el-descriptions>

          <el-descriptions :column="1" border title="预约信息" class="mt-16px">
            <el-descriptions-item label="预约时间">
              {{ detail.appointmentDate ? `${detail.appointmentDate} ${detail.appointmentTime || ''}` : '—' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系人">
              {{ detail.contactName || '—' }} {{ detail.contactPhone || '' }}
            </el-descriptions-item>
            <el-descriptions-item label="服务地址">{{ detail.serviceAddress || '—' }}</el-descriptions-item>
            <el-descriptions-item label="预约状态">{{ detail.reservationStatus || '—' }}</el-descriptions-item>
          </el-descriptions>

          <div class="section-title">现场图片</div>
          <div class="mb-12px">
            <div class="text-13px text-gray-500 mb-8px">
              用户下单图（{{ detail.orderImages?.length || 0 }} 张）
            </div>
            <div v-if="detail.orderImages?.length" class="o2o-image-row">
              <el-image
                v-for="(url, idx) in detail.orderImages"
                :key="'bo-' + idx"
                :src="url"
                :preview-src-list="detail.orderImages"
                :initial-index="idx"
                fit="cover"
                class="o2o-thumb"
                preview-teleported
              />
            </div>
            <div v-else class="text-13px text-gray-400 mb-8px">用户下单时未上传</div>
          </div>
          <div class="mb-12px">
            <div class="text-13px text-gray-500 mb-8px">
              师傅服务图（{{ detail.providerServiceImages?.length || 0 }} 张）
            </div>
            <div v-if="detail.providerServiceImages?.length" class="o2o-image-row">
              <el-image
                v-for="(url, idx) in detail.providerServiceImages"
                :key="'bs-' + idx"
                :src="url"
                :preview-src-list="detail.providerServiceImages"
                :initial-index="idx"
                fit="cover"
                class="o2o-thumb"
                preview-teleported
              />
            </div>
            <div v-else class="text-13px text-gray-400">师傅完工时未上传</div>
          </div>

          <div class="section-title">服务项目</div>
          <el-table :data="detail.items || []" size="small" stripe>
            <el-table-column label="服务" min-width="200">
              <template #default="{ row }">
                <div class="flex items-center gap-8px">
                  <el-image
                    v-if="row.picUrl"
                    :preview-src-list="[row.picUrl]"
                    :src="row.picUrl"
                    class="!h-40px !w-40px flex-none"
                    fit="cover"
                  />
                  <span>{{ cleanServiceName(row.spuName) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="数量" width="70" prop="count" />
            <el-table-column label="金额" width="100">
              <template #default="{ row }">{{ fenToYuan(row.payPrice) }} 元</template>
            </el-table-column>
          </el-table>

          <div class="section-title">派单流水</div>
          <el-table v-loading="dispatchLoading" :data="dispatchList" size="small" stripe>
            <el-table-column label="类型" width="90" prop="dispatchType" />
            <el-table-column label="目标" min-width="120" prop="targetName" />
            <el-table-column label="状态" width="90">
              <template #default="{ row }">{{ dispatchStatusLabel(row.status) }}</template>
            </el-table-column>
            <el-table-column label="时间" width="170" prop="createTime" :formatter="dateFormatter" />
          </el-table>
          <el-empty v-if="!dispatchLoading && !dispatchList.length" description="暂无派单流水" />
        </template>
      </div>
    </el-drawer>

    <el-dialog v-model="assignVisible" title="内派师傅" width="520px" @closed="resetAssignForm">
      <el-alert
        class="mb-12px"
        :closable="false"
        show-icon
        title="仅展示本机构「接单中」且已审核 ACTIVE、具备该服务能力的员工。已下线师傅不可内派；请师傅在 App 打开接单后再派。"
        type="info"
      />
      <el-empty
        v-if="!assignLoading && !assignableProviders.length"
        description="暂无可内派员工"
      >
        <template #description>
          <div class="text-14px text-gray-500 leading-22px">
            请先通过「员工管理 → 邀请码」邀请员工入驻，并确保其已绑定服务能力且 App 接单中。
          </div>
        </template>
        <el-button type="primary" @click="goInviteEmployees">去邀请员工</el-button>
      </el-empty>
      <el-form v-else label-width="80px">
        <el-form-item label="选择师傅">
          <el-select
            v-model="assignForm.providerId"
            class="!w-full"
            filterable
            placeholder="请选择本机构师傅"
            v-loading="assignLoading"
          >
            <el-option
              v-for="item in assignableProviders"
              :key="item.id"
              :label="assignProviderLabel(item)"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="assignVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="assignSubmitting"
          :disabled="!assignableProviders.length"
          @click="submitAssign"
        >确认内派</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="returnVisible" title="退回平台" width="480px" @closed="resetReturnForm">
      <el-alert
        class="mb-16px"
        :closable="false"
        show-icon
        title="订单将离开本机构，由平台重新派单；本机构订单列表中将不再显示该单。"
        type="warning"
      />
      <el-form label-width="80px">
        <el-form-item label="退回原因">
          <el-input
            v-model="returnForm.reason"
            :maxlength="200"
            placeholder="可选，如：暂无可用师傅"
            show-word-limit
            type="textarea"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="returnVisible = false">取消</el-button>
        <el-button type="warning" :loading="returnSubmitting" @click="submitReturn">确认退回</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import {
  assignO2oMerchantOrder,
  getO2oMerchantAssignableProviders,
  getO2oMerchantOrder,
  getO2oMerchantOrderDispatchList,
  getO2oMerchantOrderPage,
  returnO2oMerchantOrderToPlatform,
  type O2oOrderAssignableProviderVO,
  type O2oOrderDispatchVO,
  type O2oOrderMerchantDetailVO,
  type O2oOrderMerchantRespVO,
  type O2oOrderMerchantTab
} from '@/api/merchant/o2o/order'
import { getO2oInstitutionContext } from '@/api/merchant/o2o/institution'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { fenToYuan } from '@/utils'
import { dateFormatter, formatDate } from '@/utils/formatTime'

defineOptions({ name: 'MerchantO2oOrder' })

const message = useMessage()
const { push } = useRouter()

function goInviteEmployees() {
  assignVisible.value = false
  push({ path: '/store/o2o/provider', query: { tab: 'invite' } })
}

const activeTab = ref<O2oOrderMerchantTab>('PENDING')
const loading = ref(false)
const list = ref<O2oOrderMerchantRespVO[]>([])
const total = ref(0)

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  no: '',
  createTime: [] as string[]
})

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<O2oOrderMerchantDetailVO | null>(null)
const dispatchLoading = ref(false)
const dispatchList = ref<O2oOrderDispatchVO[]>([])

const assignVisible = ref(false)
const assignLoading = ref(false)
const assignSubmitting = ref(false)
const assignableProviders = ref<O2oOrderAssignableProviderVO[]>([])
const assignForm = reactive({ providerId: undefined as number | undefined })
const assignOrderId = ref<number>()

const returnVisible = ref(false)
const returnSubmitting = ref(false)
const returnForm = reactive({ reason: '' })
const returnOrderId = ref<number>()

const depositSatisfied = ref<boolean | undefined>(undefined)

const statusOptions = getIntDictOptions(DICT_TYPE.TRADE_ORDER_STATUS)
const statusMap = computed(() =>
  Object.fromEntries(statusOptions.map((item) => [item.value, item.label]))
)

const detailTitle = computed(() => (detail.value ? `订单详情 · ${detail.value.no}` : '订单详情'))

function statusLabel(status?: number) {
  return statusMap.value[status as number] || `状态#${status}`
}

function dispatchStatusLabel(status?: string) {
  return (
    {
      PENDING: '待处理',
      ACCEPTED: '已接受',
      REJECTED: '已拒绝',
      CANCELLED: '已关闭',
      TIMEOUT: '已超时'
    } as Record<string, string>
  )[status || ''] || status || '—'
}

function statusTagType(status?: number) {
  if (status === 51) return 'warning'
  if (status === 52 || status === 53) return 'primary'
  if (status === 54) return 'success'
  if (status === 30 || status === 55) return 'info'
  if (status === 40) return 'info'
  if (status === 56) return 'danger'
  return ''
}

function fulfillmentLabel(type?: string) {
  if (type === 'HOME') return '上门服务'
  if (type === 'STORE') return '到店服务'
  return type || '—'
}

function assignProviderLabel(item: O2oOrderAssignableProviderVO) {
  const name = item.realName || `师傅#${item.id}`
  const mobile = item.mobile ? `（${item.mobile}）` : ''
  const online = item.acceptingOrders === false ? ' · 已下线' : item.acceptingOrders ? ' · 接单中' : ''
  return `${name}${mobile}${online}`
}

function formatDateTime(value?: string) {
  return value ? formatDate(value) : ''
}

function cleanServiceName(name?: string) {
  if (!name) return '上门服务'
  return String(name)
    .replace(/^\[O2O\]\s*/i, '')
    .replace(/^【O2O】\s*/i, '')
    .trim() || '上门服务'
}

async function getList() {
  loading.value = true
  try {
    const data = await getO2oMerchantOrderPage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      tab: activeTab.value,
      no: queryParams.no || undefined,
      createTime: queryParams.createTime?.length ? queryParams.createTime : undefined
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  queryParams.pageNo = 1
  getList()
}

function resetQuery() {
  queryParams.no = ''
  queryParams.createTime = []
  handleQuery()
}

function handleTabChange() {
  queryParams.pageNo = 1
  getList()
}

async function openDetail(id: number) {
  detailVisible.value = true
  detailLoading.value = true
  dispatchLoading.value = true
  detail.value = null
  dispatchList.value = []
  try {
    detail.value = await getO2oMerchantOrder(id)
    dispatchList.value = await getO2oMerchantOrderDispatchList(id)
  } catch {
    message.error('加载订单详情失败')
    detailVisible.value = false
  } finally {
    detailLoading.value = false
    dispatchLoading.value = false
  }
}

function canAssign(row: { availableActions?: string[]; refunding?: boolean }) {
  return row.availableActions?.includes('ASSIGN_PROVIDER') === true
    && !row.refunding && depositSatisfied.value !== false
}

function canReturn(row: { availableActions?: string[]; refunding?: boolean }) {
  return row.availableActions?.includes('RETURN_PLATFORM') === true && !row.refunding
}

function resetAssignForm() {
  assignForm.providerId = undefined
  assignableProviders.value = []
  assignOrderId.value = undefined
}

async function openAssignDialog() {
  if (!detail.value) return
  assignOrderId.value = detail.value.id
  assignVisible.value = true
  assignLoading.value = true
  try {
    assignableProviders.value = await getO2oMerchantAssignableProviders(detail.value.id)
    if (!assignableProviders.value.length) {
      message.warning(
        '暂无可内派师傅：请确认员工已通过平台审核(ACTIVE)、已绑定本服务能力，且 App 端处于「接单中」'
      )
    }
  } catch {
    message.error('加载师傅列表失败')
    assignVisible.value = false
  } finally {
    assignLoading.value = false
  }
}

async function submitAssign() {
  if (!assignOrderId.value || !assignForm.providerId) {
    message.warning('请选择师傅')
    return
  }
  if (depositSatisfied.value === false) {
    message.warning('保证金可用余额不足，无法内派。请线下打款后联系平台确认入账')
    return
  }
  assignSubmitting.value = true
  try {
    await assignO2oMerchantOrder({ orderId: assignOrderId.value, providerId: assignForm.providerId })
    message.success('内派成功')
    assignVisible.value = false
    await openDetail(assignOrderId.value)
    await getList()
  } catch (e: any) {
    const msg = e?.message || ''
    if (msg.includes('保证金') || msg.includes('承诺金')) {
      depositSatisfied.value = false
    }
  } finally {
    assignSubmitting.value = false
  }
}

function resetReturnForm() {
  returnForm.reason = ''
  returnOrderId.value = undefined
}

function openReturnDialog(row: { id: number }) {
  returnOrderId.value = row.id
  returnVisible.value = true
}

async function submitReturn() {
  if (!returnOrderId.value) return
  try {
    await message.confirm('确认将该订单退回平台？退回后本机构将无法再处理该单。')
  } catch {
    return
  }
  returnSubmitting.value = true
  try {
    await returnO2oMerchantOrderToPlatform({
      orderId: returnOrderId.value,
      reason: returnForm.reason || undefined
    })
    message.success('已退回平台')
    returnVisible.value = false
    if (detailVisible.value && detail.value?.id === returnOrderId.value) {
      detailVisible.value = false
    }
    await getList()
  } finally {
    returnSubmitting.value = false
  }
}

onMounted(async () => {
  try {
    const ctx = await getO2oInstitutionContext()
    depositSatisfied.value = ctx.depositSatisfied
  } catch {
    depositSatisfied.value = undefined
  }
  await getList()
})
</script>

<style lang="scss" scoped>
.section-title {
  margin: 16px 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.o2o-image-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.o2o-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}
</style>
